//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import ClientId from '@models/ClientId';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';
import {
  IConfiguration,
  ITaggedByBVAutomationConfiguration,
} from './automationApiModels';

const AUTOMATION_API_BASE_URL = '/api/v1/connect/taggedByCuralate';

function gatewaySyndicationFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch('syndication_gateway', endpoint, init);
}

/**
 * Gets the configurations associated with the given publisher client
 * /api/v1/connect/taggedByCuralate/configurations/:publisherClientId
 * @param publisherClientId
 *
 * @returns List of configuration as a promise
 */
export async function getClientTaggedByBazaarvoiceConfigurations(
  publisherClientId: ClientId,
): Promise<ITaggedByBVAutomationConfiguration> {
  try {
    const res = await gatewaySyndicationFetch(
      `${AUTOMATION_API_BASE_URL}/configurations/${publisherClientId}`,
    );
    const response = res.json<ITaggedByBVAutomationConfiguration>();
    return response;
  } catch {
    throw new Error(
      `Bad getClientTaggedByBazaarvoiceConfigurations response, publisherId: ${publisherClientId}`,
    );
  }
}

/**
 * Gets the active configurations associated with the given publisher client
 * /api/v1/connect/taggedByCuralate/configurations/:publisherClientId/:isActive
 * @param publisherClientId
 *
 * @returns List of configuration as a promise
 */
export async function getClientTaggedByBazaarvoiceActiveConfigurations(
  publisherClientId: ClientId,
): Promise<ITaggedByBVAutomationConfiguration> {
  try {
    const res = await gatewaySyndicationFetch(
      `${AUTOMATION_API_BASE_URL}/configurations/${publisherClientId}/true`,
    );
    const response = res.json<ITaggedByBVAutomationConfiguration>();
    return response;
  } catch {
    throw new Error(
      `Bad getClientTaggedByBazaarvoiceActiveConfigurations response, publisherId: ${publisherClientId}`,
    );
  }
}

/**
 * Deletes a configuration
 *
 * @param publisherClientId
 * @param configId
 * @returns An empty promise
 */
export const deleteConfiguration = async (
  publisherClientId: ClientId,
  configId: number,
): Promise<Response> => {
  return await gatewaySyndicationFetch(
    `${AUTOMATION_API_BASE_URL}/configurations/${publisherClientId}/config/${configId}`,
    {
      method: 'DELETE',
    },
  );
};

/**
 * Save a configuration
 *
 * @param publisherClientId
 * @param configuration
 * @returns An empty promise
 */
export const saveConfiguration = async (
  publisherClientId: ClientId,
  configuration: IConfiguration,
): Promise<Response> => {
  const requestBody = {
    taggedByCuralateConfiguration: configuration,
  };
  return await gatewaySyndicationFetch(
    `${AUTOMATION_API_BASE_URL}/configurations/${publisherClientId}`,
    {
      method: 'POST',
      body: JSON.stringify(requestBody),
    },
  );
};

/**
 * Update a configuration
 *
 * @param publisherClientId
 * @param configuration
 * @returns An empty promise
 */
export const updateConfiguration = async (
  publisherClientId: ClientId,
  configuration: IConfiguration[],
): Promise<Response> => {
  const requestBody = {
    taggedByCuralateConfiguration: configuration,
  };
  return gatewaySyndicationFetch(
    `${AUTOMATION_API_BASE_URL}/configurations/${publisherClientId}`,
    {
      method: 'PUT',
      body: JSON.stringify(requestBody),
    },
  );
};
